import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { auth } from "@/lib/auth";
import CalendarBoard from "@/components/CalendarBoard";
import { useLang } from "@/context/LangContext";
import {
    Users, CalendarCheck, Mail, Radio, Mic2, Ticket, CreditCard, TrendingUp,
    Activity, ExternalLink, RefreshCw, Music2, Newspaper, AlertTriangle, CheckCircle2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => new Intl.NumberFormat("fr-CH").format(n || 0);

function StatCard({ icon: Icon, label, value, sub, delay = 0, testid }) {
    return (
        <motion.div
            data-testid={testid}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay, duration: 0.5 }}
            className="border border-white/10 bg-white/[0.03] p-5"
        >
            <p className="flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] text-cream/50">
                <Icon size={12} className="text-violet" /> {label}
            </p>
            <p className="mt-3 font-display text-3xl font-extrabold tracking-tight text-cream">{value}</p>
            {sub && <p className="mt-1 font-mono text-[8px] tracking-[0.2em] text-cream/40">{sub}</p>}
        </motion.div>
    );
}

function Bar({ label, value, max, delay }) {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
        <div className="mb-3">
            <div className="flex justify-between font-mono text-[9px] tracking-[0.2em] text-cream/60">
                <span>{label}</span><span className="text-gold">{fmt(value)}</span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-white/10">
                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${pct}%` }}
                    viewport={{ once: true }}
                    transition={{ delay, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-violet to-gold"
                />
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const { lang } = useLang();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(false);

    const load = async () => {
        setLoading(true);
        setErr(false);
        try {
            const r = await auth.fetch(`${API}/api/admin/stats`);
            if (!r.ok) throw new Error();
            setStats(await r.json());
        } catch {
            setErr(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const health = stats?.last_health;
    const healthy = health?.mongo === "ok";

    return (
        <div data-testid="admin-dashboard" className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-gold">
                    <Activity size={13} /> {lang === "fr" ? "VUE D'ENSEMBLE — TEMPS RÉEL" : "OVERVIEW — REAL TIME"}
                </p>
                <div className="flex items-center gap-3">
                    <button data-testid="dash-refresh" onClick={load} className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[8px] tracking-[0.25em] text-cream/60 transition-all hover:border-gold hover:text-gold">
                        <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> {lang === "fr" ? "ACTUALISER" : "REFRESH"}
                    </button>
                    <a
                        data-testid="dash-emergent-btn"
                        href="https://app.emergent.sh"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 border border-violet/60 bg-violet/10 px-4 py-2 font-mono text-[8px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                    >
                        <ExternalLink size={11} /> {lang === "fr" ? "GÉRER SUR EMERGENT" : "MANAGE ON EMERGENT"}
                    </a>
                </div>
            </div>

            {/* santé du site */}
            <motion.div
                data-testid="dash-health"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`flex items-center justify-between border p-4 ${healthy ? "border-green-500/40 bg-green-500/[0.06]" : "border-red-500/40 bg-red-500/[0.06]"}`}
            >
                <p className="flex items-center gap-2 font-mono text-[9px] tracking-[0.25em]">
                    {healthy
                        ? <><CheckCircle2 size={13} className="text-green-400" /> <span className="text-green-400">{lang === "fr" ? "SITE OPÉRATIONNEL" : "SITE OPERATIONAL"}</span></>
                        : <><AlertTriangle size={13} className="text-red-400" /> <span className="text-red-400">{lang === "fr" ? "PROBLÈME DÉTECTÉ" : "ISSUE DETECTED"}</span></>}
                </p>
                {health?.latency_ms != null && (
                    <p className="font-mono text-[8px] tracking-[0.2em] text-cream/50">
                        {lang === "fr" ? "LATENCE" : "LATENCY"} : {health.latency_ms} MS
                    </p>
                )}
            </motion.div>

            {err && <p className="font-mono text-[9px] tracking-[0.2em] text-red-400">{lang === "fr" ? "ERREUR DE CHARGEMENT" : "LOADING ERROR"}</p>}

            {/* grille de stats */}
            {stats && (
                <>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <StatCard testid="stat-users" icon={Users} label={lang === "fr" ? "COMPTES CLIENTS" : "CLIENT ACCOUNTS"} value={fmt(stats.users)} delay={0} />
                        <StatCard testid="stat-bookings" icon={CalendarCheck} label={lang === "fr" ? "RÉSERVATIONS" : "BOOKINGS"} value={fmt(stats.bookings)} delay={0.05} />
                        <StatCard testid="stat-revenue" icon={CreditCard} label={lang === "fr" ? "REVENU (ACOMPTES)" : "REVENUE (DEPOSITS)"} value={`${fmt(stats.revenue_chf)} CHF`} sub={`${fmt(stats.payments_paid)} ${lang === "fr" ? "PAYÉS" : "PAID"}`} delay={0.1} />
                        <StatCard testid="stat-contacts" icon={Mail} label={lang === "fr" ? "CONTACTS" : "CONTACTS"} value={fmt(stats.contacts)} delay={0.15} />
                        <StatCard testid="stat-artists" icon={Mic2} label={lang === "fr" ? "ARTISTES RÉCLAMÉS" : "CLAIMED ARTISTS"} value={fmt(stats.artists)} delay={0.2} />
                        <StatCard testid="stat-radio" icon={Radio} label={lang === "fr" ? "SONS GLM RADIO" : "GLM RADIO TRACKS"} value={fmt(stats.radio_tracks)} delay={0.25} />
                        <StatCard testid="stat-chart" icon={TrendingUp} label={lang === "fr" ? "ARTISTES TOP 10 CH" : "TOP 10 CH ARTISTS"} value={fmt(stats.chart_artists)} delay={0.3} />
                        <StatCard testid="stat-articles" icon={Newspaper} label={lang === "fr" ? "ARTICLES CULTURE" : "CULTURE ARTICLES"} value={fmt(stats.articles)} delay={0.35} />
                    </div>

                    {/* calendrier partagé admin + 6xo */}
                    <CalendarBoard provider="gloom" />

                    {/* graphique d'activité */}
                    <div className="border border-white/10 bg-white/[0.02] p-6">
                        <p className="mb-4 font-mono text-[10px] tracking-[0.3em] text-gold">{lang === "fr" ? "ACTIVITÉ DU SITE" : "SITE ACTIVITY"}</p>
                        <Bar label={lang === "fr" ? "Comptes clients" : "Client accounts"} value={stats.users} max={Math.max(stats.users, stats.bookings, stats.contacts, 1)} delay={0} />
                        <Bar label={lang === "fr" ? "Réservations" : "Bookings"} value={stats.bookings} max={Math.max(stats.users, stats.bookings, stats.contacts, 1)} delay={0.1} />
                        <Bar label={lang === "fr" ? "Contacts / demandes" : "Contacts / enquiries"} value={stats.contacts} max={Math.max(stats.users, stats.bookings, stats.contacts, 1)} delay={0.2} />
                        <Bar label={lang === "fr" ? "Soumissions radio" : "Radio submissions"} value={stats.radio_submissions} max={Math.max(stats.users, stats.bookings, stats.contacts, 1)} delay={0.3} />
                        <Bar label={lang === "fr" ? "Billets VIP" : "VIP tickets"} value={stats.vip_tickets} max={Math.max(stats.users, stats.bookings, stats.contacts, 1)} delay={0.4} />
                    </div>

                    {/* trafic SEO */}
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="border border-white/10 bg-white/[0.02] p-6"
                    >
                        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">
                            {lang === "fr" ? "TRAFIC — 7 DERNIERS JOURS" : "TRAFFIC — LAST 7 DAYS"}
                        </p>
                        <p className="mt-3 font-display text-3xl font-extrabold text-cream">{stats.traffic_7d || 0}</p>
                        <p className="mt-1 font-mono text-[9px] tracking-[0.2em] text-cream/40">
                            {lang === "fr" ? "pages vues" : "page views"}
                        </p>
                        {stats.traffic_pages && stats.traffic_pages.length > 0 && (
                            <div className="mt-4 space-y-2">
                                {stats.traffic_pages.slice(0, 3).map((p, i) => (
                                    <div key={p._id} className="flex items-center justify-between border-b border-white/5 pb-2">
                                        <p className="font-mono text-[9px] tracking-[0.15em] text-cream/70 truncate">{p._id}</p>
                                        <p className="font-mono text-[9px] tracking-[0.15em] text-violet">{p.views}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>

                </>
            )}
        </div>
    );
}







